import {
  filter,
  ignoreElements,
  map,
  mergeMap,
  tap,
  throttleTime,
} from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { setWebexShareState } from "../../state/config/config.actions";
import { AppMode } from "../../state/mode/mode.reducer";
import * as workspacesActions from "../../state/workspaces/workspaces.actions";
import {
  selectFullscreenWorkspaceId,
  selectStoredSearchParams,
  selectWorkspaceById,
  selectWorkspaces,
  selectWorkspacesRoles,
  selectWorkspacesWaitingToEditAllowTemplateEdit,
  selectWorkspacesWaitingToEditId,
} from "../../state/workspaces/workspaces.selector";
import type { AppEpic } from "../types";

const isNonEmptyString = (value: any): value is string =>
  typeof value === "string" && !!value.length;

export const setWorkspaceTitleEpic: AppEpic = (
  action$,
  state$,
  { updateDocumentTitle, getDocumentTitle },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.editWorkspace)),
    filter(() => getDocumentTitle() === "Hoylu"),
    map(
      (a) =>
        selectWorkspaceById(state$.value, a.payload.workspaceId)?.workspaceName,
    ),
    filter(isNonEmptyString),
    tap(updateDocumentTitle),
    ignoreElements(),
  );

export const showDashboardEpic: AppEpic = (
  action$,
  state$,
  { updateDocumentTitle, sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.showDashboard)),
    tap(() => updateDocumentTitle("Hoylu")),
    map(() => selectFullscreenWorkspaceId(state$.value)),
    tap((fullscreenWorkspaceId) => {
      if (fullscreenWorkspaceId) {
        sendMessageToWorkspace(fullscreenWorkspaceId, {
          action: "TOGGLE_FOCUS",
          isFocused: false,
        });
      }
    }),
    map(() => workspacesActions.unfocusWorkspaces()),
  );

export const clearShareUrlEpic: AppEpic = (
  action$,
  state$,
  { clearWebexShareUrl, log },
) =>
  action$.pipe(
    filter(isActionOf([workspacesActions.showDashboard, setWebexShareState])),
    filter(
      () =>
        !!state$.value.context.config.webexMode &&
        !state$.value.context.config.webexMode?.isShared,
    ),
    filter(() => state$.value.mode === AppMode.DASHBOARD),
    tap(async () => {
      try {
        await clearWebexShareUrl();
      } catch (e) {
        // happens when clearing & Stop Session is there on dashboard
        log.error(`clearShareUrlEpic: error: ${e}`);
      }
    }),
    ignoreElements(),
  );

export const autoFetchWorkspaceDetailsEpic: AppEpic = (_action$, state$) =>
  state$.pipe(
    filter((s) => !!s.context.workspaces.waitingToEditID),
    filter((s) => !s.context.workspaces.isLoadingWorkspaceDetail),
    filter(
      (s) =>
        s.mode === AppMode.DASHBOARD || // -> "open shared workspace" button
        s.mode === AppMode.LOADING_DASHBOARD, // -> loading workspace through URL
    ),
    filter((s) => !!s.context.user.token),
    filter((s) => {
      const workspace = selectWorkspaceById(
        s,
        s.context.workspaces.waitingToEditID!,
      );
      return workspace === undefined;
    }),
    map((s) => {
      return workspacesActions.getWorkspaceDetails.request(
        s.context.workspaces.waitingToEditID!,
      );
    }),
    throttleTime(100), // prevent multiple getWorkspaceDetails.requests for matching states
  );

export const autoEditQueuedWorkspaceWhenReadyEpic: AppEpic = (
  _action$,
  state$,
  { openWorkspaceInNewWindow },
) =>
  state$.pipe(
    filter((s) => !!selectWorkspacesWaitingToEditId(s)),
    filter(
      (s) =>
        s.mode === AppMode.LOADING_WORKSPACE ||
        s.mode === AppMode.LOADING_DASHBOARD || // -> loading workspace through URL
        s.mode === AppMode.DASHBOARD || // -> "open shared workspace" button
        s.mode === AppMode.EDITING,
    ),
    filter((s) => !!selectWorkspaces(s).length),
    filter((s) => {
      const waitingToEditID = selectWorkspacesWaitingToEditId(s)!;
      const workspace = selectWorkspaceById(s, waitingToEditID);
      const fullscreenWorkspaceId = selectFullscreenWorkspaceId(s);
      const workspaceRoles = selectWorkspacesRoles(s);
      return (
        !!workspace &&
        workspace.workspaceId !== fullscreenWorkspaceId &&
        !!workspaceRoles[workspace.workspaceId]
      );
    }),
    mergeMap((s) => {
      const waitingToEditID = selectWorkspacesWaitingToEditId(s)!;
      const myWorkspaceMetadata = selectWorkspaceById(s, waitingToEditID);
      const waitingToEditAllowTemplateEdit =
        selectWorkspacesWaitingToEditAllowTemplateEdit(s);

      if (
        myWorkspaceMetadata &&
        ["SKETCH", "FLOW"].includes(myWorkspaceMetadata.workspaceType)
      ) {
        openWorkspaceInNewWindow(myWorkspaceMetadata, s.context.config);
        return [workspacesActions.showDashboard()];
      }

      if (myWorkspaceMetadata?.templateId && !waitingToEditAllowTemplateEdit) {
        return [
          workspacesActions.createWorkspaceFromTemplate(myWorkspaceMetadata),
        ];
      }

      const storedSearchParams = selectStoredSearchParams(s);
      const urlSearchParams = new URLSearchParams(
        storedSearchParams[waitingToEditID] ?? "",
      );

      return [
        workspacesActions.editWorkspace({
          workspaceId: waitingToEditID,
          urlSearchParams,
        }),
      ];
    }),
  );

export const pushStateOnEditWorkspaceEpic: AppEpic = (
  action$,
  _state$,
  { syncHistory },
) =>
  action$.pipe(
    filter(
      isActionOf([
        workspacesActions.editWorkspace,
        workspacesActions.changeLocalization,
      ]),
    ),
    tap((a) => syncHistory(a.payload.workspaceId, a.payload.urlSearchParams)),
    ignoreElements(),
  );

export const popStateOnShowDashboardEpic: AppEpic = (
  action$,
  _state$,
  { syncHistory },
) =>
  action$.pipe(
    filter(isActionOf(workspacesActions.showDashboard)),
    tap(() => syncHistory("DASHBOARD")),
    ignoreElements(),
  );

export const setFocusLoadedWorkspaceEpic: AppEpic = (
  action$,
  state$,
  { focusWorkspace, sendMessageToWorkspace },
) =>
  action$.pipe(
    filter(
      isActionOf([
        workspacesActions.editWorkspace,
        workspacesActions.setFocusAction,
      ]),
    ),
    map(() => selectFullscreenWorkspaceId(state$.value)),
    tap((fullscreenWorkspaceId) => {
      if (fullscreenWorkspaceId) {
        focusWorkspace(fullscreenWorkspaceId);
        sendMessageToWorkspace(fullscreenWorkspaceId, {
          action: "TOGGLE_FOCUS",
          isFocused: true,
        });
      }
    }),
    ignoreElements(),
  );
